import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, CalendarClock, ListChecks, Wallet } from "lucide-react";
import { Card, CardBody, CardHeader, CardTitle } from "@hp/ui";
import { fetchPipelineSummary, FORWARD_STAGES } from "../api/pipeline";
import { StatTile } from "../components/StatTile";
import { PipelineFunnelChart } from "../components/pipeline/PipelineFunnelChart";
import { ForumBreakdownList } from "../components/pipeline/ForumBreakdownList";
import { ErrorBlock, LoadingBlock } from "../components/QueryStatus";
import { formatCount, formatINRRange } from "../lib/format";

export function PipelinePage() {
  const { data, isPending, isError } = useQuery({
    queryKey: ["dashboard", "pipeline-summary"],
    queryFn: () => fetchPipelineSummary(),
  });

  if (isPending) return <LoadingBlock label="Loading claim pipeline" surface="canvas" />;
  if (isError || !data) return <ErrorBlock message="Could not load the claim pipeline summary." surface="canvas" />;

  const forwardStages = FORWARD_STAGES.map(
    (status) => data.by_status.find((s) => s.status === status) ?? { status, count: 0, value_low_total: 0, value_high_total: 0 },
  );
  const rejected = data.by_status.find((s) => s.status === "rejected");
  const deferred = data.by_status.find((s) => s.status === "deferred");

  return (
    <div className="page-stack">
      <div>
        <div className="eyebrow">Pipeline Report</div>
        <h2 className="mt-1 text-lg font-semibold text-zinc-900">Claim Pipeline</h2>
        <p className="mt-1 text-sm text-zinc-500">
          Every candidate claim the engine has surfaced, by stage, as of {new Date(data.as_of).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile
          label="Total claim candidates"
          value={formatCount(data.total_claim_candidates)}
          sublabel="across every pipeline stage"
          icon={ListChecks}
          tone="brand"
        />
        <StatTile
          label="Total value at stake"
          value={formatINRRange(data.total_value_low, data.total_value_high)}
          sublabel="estimated recovery range"
          icon={Wallet}
          tone="brand"
        />
        <StatTile
          label="Filed, awaiting hearing"
          value={formatCount(data.upcoming_hearings_count)}
          sublabel="claims currently filed"
          icon={CalendarClock}
          tone="neutral"
        />
        <StatTile
          label="Limitation-flagged"
          value={formatCount(data.limitation_flagged_count)}
          sublabel="need filing before a deadline"
          icon={AlertTriangle}
          tone={data.limitation_flagged_count > 0 ? "warning" : "neutral"}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Value by pipeline stage</CardTitle>
        </CardHeader>
        <CardBody className="space-y-4">
          <PipelineFunnelChart stages={forwardStages} />
          {(rejected?.count ?? 0) > 0 || (deferred?.count ?? 0) > 0 ? (
            <p className="border-t border-zinc-100 pt-3 text-xs text-zinc-500">
              Additionally, <span className="font-mono tabular-nums">{formatCount(rejected?.count ?? 0)}</span>{" "}
              rejected (
              <span className="font-mono tabular-nums">
                {formatINRRange(rejected?.value_low_total ?? 0, rejected?.value_high_total ?? 0)}
              </span>
              ) and <span className="font-mono tabular-nums">{formatCount(deferred?.count ?? 0)}</span> deferred (
              <span className="font-mono tabular-nums">
                {formatINRRange(deferred?.value_low_total ?? 0, deferred?.value_high_total ?? 0)}
              </span>
              ) candidates sit outside the active pipeline.
            </p>
          ) : null}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Claim candidates by recommended forum</CardTitle>
        </CardHeader>
        <CardBody>
          <ForumBreakdownList data={data.by_forum} />
        </CardBody>
      </Card>
    </div>
  );
}
